import type { ConfigurationName, SpecificConfiguration } from '@shady-pines/db';
import { isNil } from 'lodash';
import { create } from 'zustand';
import { GlobalContextApi } from './global-context-api';
import type { GlobalContextState } from './types';

const initialState = {
  loading: {
    userFeatures: false,
    globalFeatures: false,
    configurations: false,
    initial: true,
  },

  configurations: null,
  userFeatures: null,
  globalFeatures: null,
} as const satisfies GlobalContextState;

type UtilityFunctions = {
  getConfiguration: <T extends ConfigurationName, ArrayType extends string = string>(
    name: T,
  ) => SpecificConfiguration<T, ArrayType> | undefined;
  setInitialLoadCompleted: () => void;
};

export const useGlobalContextStore = create<
  GlobalContextState & GlobalContextApi & UtilityFunctions
>((set, get) => ({
  ...initialState,
  ...GlobalContextApi(get, set),

  getConfiguration: <T extends ConfigurationName, ArrayType extends string = string>(name: T) => {
    const configurations = get().configurations;
    const configuration = configurations?.find((c) => c.name === name);
    if (isNil(configuration)) {
      return undefined;
    }
    const typedConfiguration = configuration as SpecificConfiguration<T, ArrayType>;
    if (configuration.parameter_type === 'number') {
      return {
        ...typedConfiguration,
        // gql returns numeric columns as strings...
        parameter_number: Number(typedConfiguration.parameter_number ?? 0),
      };
    }
    if (name === 'filtered_drawers') {
      const arrayConfiguration = configuration as SpecificConfiguration<'filtered_drawers'>;
      return {
        ...arrayConfiguration,
        parameter_array: (typedConfiguration.parameter_string ?? '')
          .trim()
          .split(',') as ArrayType[],
      } as SpecificConfiguration<'filtered_drawers', ArrayType>;
    }

    return configuration as SpecificConfiguration<T, ArrayType>;
  },
  setInitialLoadCompleted: () =>
    set(
      (s) =>
        ({
          loading: {
            ...s.loading,
            initial: false,
          },
        }) as const satisfies Partial<GlobalContextState>,
    ),
}));
